/**
 * AutomationFormDialog — shell shared (Sprint 3 / BUG-V2-058).
 *
 * Decisão arquitetural: **Opção C híbrida** (Cowork 2026-05-25).
 *
 * Este componente é o ÚNICO entry point pra criar/editar automações
 * inline em qualquer contexto. Dispatcha pro form correto via discriminator
 * `contexto.kind`:
 *
 *   - `workflow`    → `<WorkflowAutomationForm>`    (wizard 3 passos, 12 actions)
 *   - `questionario`→ `<QuestionarioAutomationForm>` (wrapper sobre Workflow form,
 *                                                    trigger questionario_resposta)
 *   - `task_type`   → `<TaskTypeAutomationForm>`    (form simples, 4 triggers,
 *                                                    3 actions, backend separado)
 *
 * Forms são responsáveis pelo Dialog wrapper interno + footer + submit.
 * O shell só roteia + cria backward-compat path por re-export.
 *
 * **Backward-compat**: a API mantém o set comum de props
 * (`open/onClose/onSubmit/columns/deals/initial/.../getColumnLabel`) usado
 * historicamente. Contexto novo é opcional pra workflow (default = workflow
 * vazio), obrigatório pra task_type/questionario.
 *
 * Histórico:
 *   - 2026-05-25 (Sprint 3): criado. Original em
 *     `src/pages/menu/comercial/AutomationFormDialog.tsx` movido pra
 *     `src/components/automations/forms/WorkflowAutomationForm.tsx`.
 */

import type { Automation, CreateAutomationInput } from '@/lib/automations-store';
import type { CreateTaskTypeAutomationInput, TaskTypeAutomation } from '@/lib/task-type-automations-store';
import type { Deal } from '@/lib/comercial-types';
import type { KanbanColumn } from '@/lib/use-kanban-columns';

import type { AutomationContexto } from './types';
import { WorkflowAutomationForm } from './forms/WorkflowAutomationForm';
import { QuestionarioAutomationForm } from './forms/QuestionarioAutomationForm';
import { TaskTypeAutomationForm } from './forms/TaskTypeAutomationForm';

/**
 * Props do shell. Set comum + variantes específicas por kind.
 */
export interface AutomationFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Contexto que determina qual form renderizar. */
  contexto: AutomationContexto;
  // ─── Workflow / Questionario shared props ─────────────────────────────
  /** Submit handler pra kinds 'workflow' e 'questionario' (backend automations). */
  onSubmit?: (input: Omit<CreateAutomationInput, 'workflowSlug'>) => Promise<void> | void;
  /** Colunas do workflow alvo. */
  columns?: KanbanColumn[];
  /** Deals do workflow pro sandbox. */
  deals?: Deal[];
  /** Automação existente (edit mode pra workflow/questionario). */
  initial?: Automation;
  /** ID pra sandbox preview. */
  automationId?: string;
  /** Resolve label de coluna. */
  getColumnLabel?: (slug: string) => string;
  // ─── TaskType-specific props ──────────────────────────────────────────
  /** Submit handler pra kind 'task_type' (backend task-type-automations). */
  onSubmitTaskType?: (input: CreateTaskTypeAutomationInput) => Promise<void>;
  /** TaskType automation pra edit (kind 'task_type'). */
  editingTaskType?: TaskTypeAutomation | null;
  /** Estado de loading pro botão de submit (kind 'task_type'). */
  taskTypeBusy?: boolean;
}

export function AutomationFormDialog(props: AutomationFormDialogProps) {
  const { contexto } = props;

  if (contexto.kind === 'task_type') {
    if (!props.onSubmitTaskType) {
      throw new Error(
        '[AutomationFormDialog] kind="task_type" exige prop `onSubmitTaskType`.',
      );
    }
    return (
      <TaskTypeAutomationForm
        open={props.open}
        onClose={props.onClose}
        onSubmit={props.onSubmitTaskType}
        editing={props.editingTaskType ?? null}
        busy={props.taskTypeBusy}
        contexto={contexto}
      />
    );
  }

  // Workflow + Questionario backend comum mas props específicos por kind.
  if (!props.onSubmit) {
    throw new Error(
      '[AutomationFormDialog] kinds workflow/questionario exigem prop `onSubmit`.',
    );
  }

  if (contexto.kind === 'questionario') {
    // BUG-V2-058 Sub-fase 6: form nativo de questionário (não wrapper).
    // NÃO precisa de columns/deals/getColumnLabel (wizard não usa coluna kanban).
    return (
      <QuestionarioAutomationForm
        open={props.open}
        onClose={props.onClose}
        onSubmit={props.onSubmit}
        initial={props.initial}
        contexto={contexto}
      />
    );
  }

  // contexto.kind === 'workflow' — precisa de columns + getColumnLabel.
  if (!props.columns) {
    throw new Error('[AutomationFormDialog] kind="workflow" exige `columns`.');
  }
  if (!props.getColumnLabel) {
    throw new Error('[AutomationFormDialog] kind="workflow" exige `getColumnLabel`.');
  }
  return (
    <WorkflowAutomationForm
      open={props.open}
      onClose={props.onClose}
      onSubmit={props.onSubmit}
      columns={props.columns}
      deals={props.deals ?? []}
      initial={props.initial}
      automationId={props.automationId}
      getColumnLabel={props.getColumnLabel}
      contexto={contexto}
    />
  );
}
